'use client';

import { getColorway, getYMargins } from 'utils/styles';
import { HubSpotProps } from './types';
import { Colorways, Margins } from 'types/constants';
import RichTextHeadline from 'components/shared/RichTextHeadline/RichTextHeadline';
import { useEffect, useRef, useState } from 'react';
import { getTagsDomain, getTagsFileUrl } from 'utils/tags';
import { liveUpdateAttrs } from 'libs/contentful/utils';

const HubSpot = ({ sys, sectionName, headline, formId }: HubSpotProps) => {
  const color = Colorways.Inverse;
  const margins = Margins.Centered;

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const tagsDomain = getTagsDomain();
  const [tagsUrl, setTagsUrl] = useState('');

  useEffect(() => {
    // Ensure the iframe reloads when the page is reloaded and pass formId
    // eslint-disable-next-line react-hooks/no-deriving-state-in-effects -- needs Date.now() cache buster, only on formId change
    setTagsUrl(`${getTagsFileUrl('chime-com-hubspot')}?formId=${formId}&_=${Date.now()}`);
  }, [formId]);

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      // Verify the message origin matches our tags domain
      if (event.origin !== tagsDomain) return;

      const data = event.data;

      // Check if the message contains form height information
      if (data && typeof data === 'object' && 'formHeight' in data) {
        const height = data.formHeight + 20; // Height + buffer to ensure form fits in container without scrolling
        if (iframeRef.current) {
          iframeRef.current.style.height = `${height}px`;
        }
      }

      // Check if the message contains redirect URL information
      if (data && typeof data === 'object' && 'redirectUrl' in data) {
        window.location.href = data.redirectUrl;
      }

      // Check if the message contains scroll to message directive
      if (data && typeof data === 'object' && 'scrollToMessage' in data) {
        const formContainer = document.querySelector(`#hubspot-iframe-${sys?.id}`) as HTMLIFrameElement;
        formContainer.scrollIntoView({ behavior: 'smooth' });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [tagsDomain, sys?.id]);

  return (
    <section
      {...(sectionName != null && { id: encodeURIComponent(sectionName) })}
      className={`flex ${getColorway(color)}`}>
      <div className="content-wrapper">
        <div className={`${getYMargins(margins)}`}>
          {headline && (
            <div
              className="font-sans font-semibold text-heading-md md:text-heading-lg text-lettuce mb-12 md:w-3/4 md:pr-12"
              {...liveUpdateAttrs(sys?.id, 'headline')}>
              <RichTextHeadline headline={headline} />
            </div>
          )}
          <div
            className="bg-bg-primary p-6 text-content-primary font-serif text-body-serif"
            {...liveUpdateAttrs(sys?.id, 'formId')}>
            {tagsUrl && (
              <iframe
                ref={iframeRef}
                title="HubSpot"
                id={`hubspot-iframe-${sys?.id}`}
                src={tagsUrl}
                sandbox="allow-scripts allow-forms allow-same-origin allow-top-navigation allow-popups allow-popups-to-escape-sandbox"
                style={{
                  width: '100%',
                  height: '500px',
                  border: 0,
                  scrollMargin: '100px',
                }}
                data-testid="hubspotFormContainer"
              />
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
export default HubSpot;
